import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  IndigoIndigo60,
  Link,
  Primary13SemiBold
} from '@sequencehq/core-components'
import chunk from 'lodash/fp/chunk'
import keyBy from 'lodash/keyBy'
import { BulkActionEntityConfig, InvoiceLite } from 'modules/BulkActions/types'
import {
  bulkActionTitle,
  downloadZipTitle
} from 'modules/BulkActions/bulkActionTitle'

const titleForInvoice = (invoice: InvoiceLite): string => {
  /**
   * Can happen in the niche scenario the enrichment fails — the invoice is
   * still accessible via its id, but we don't know anything else about it yet.
   */
  if (!invoice.success) {
    return 'Invoice'
  }

  if (invoice.data.invoiceNumber) {
    return invoice.data.invoiceNumber
  }

  return invoice.data.status === 'IN_PROGRESS'
    ? 'Upcoming invoice'
    : 'Draft invoice'
}

export const invoiceBulkActionConfig: BulkActionEntityConfig<InvoiceLite> = {
  listTitle: 'Invoice',
  invalidateOnProgress: dashboard20240730Client.postInvoice,
  titles: {
    UPDATE_INVOICES_STATUS: bulkActionTitle({
      verbing: 'Updating',
      past: 'updated',
      verb: 'update',
      noun: 'invoice status'
    }),
    VOID_INVOICES: bulkActionTitle({
      verbing: 'Voiding',
      past: 'voided',
      verb: 'void',
      noun: 'invoice'
    }),
    RECALCULATE_INVOICES: bulkActionTitle({
      verbing: 'Recalculating',
      past: 'recalculated',
      verb: 'recalculate',
      noun: 'invoice'
    }),
    DOWNLOAD_INVOICES: downloadZipTitle(
      bulkActionTitle({
        verbing: 'Downloading',
        past: 'downloaded',
        verb: 'download',
        noun: 'invoice'
      })
    ),
    FINALISE_INVOICES: bulkActionTitle({
      verbing: 'Finalizing',
      past: 'finalized',
      verb: 'finalize',
      noun: 'invoice'
    }),
    FINALISE_AND_SEND_INVOICES: bulkActionTitle({
      verbing: 'Finalizing and sending',
      past: 'finalized and sent',
      verb: 'finalize and send',
      noun: 'invoice'
    }),
    SEND_INVOICES: bulkActionTitle({
      verbing: 'Sending',
      past: 'sent',
      verb: 'send',
      noun: 'invoice'
    }),
    MARK_INVOICES_AS_SENT: bulkActionTitle({
      verbing: 'Marking',
      past: 'marked',
      verb: 'mark',
      noun: 'invoice',
      suffix: 'as sent'
    }),
    CONVERT_INVOICES_TO_DRAFT: bulkActionTitle({
      verbing: 'Converting',
      past: 'converted',
      verb: 'convert',
      noun: 'invoice',
      suffix: 'to draft'
    }),
    DELETE_INVOICES: bulkActionTitle({
      verbing: 'Deleting',
      past: 'deleted',
      verb: 'delete',
      noun: 'invoice'
    })
  },
  downloadZip: {
    types: ['DOWNLOAD_INVOICES'],
    fetch: dashboard20240730Client.getInvoiceBulkDownloadZip,
    filename: bulkActionId => `invoices-${bulkActionId}.zip`,
    errorMessage: 'Unable to download invoices zip'
  },
  enrich: {
    cacheKey: dashboard20240730Client.listInvoicesLite.queryKey,
    /**
     * The enrichment endpoint accepts at most 100 ids and is not paginated, so
     * chunk the ids into batches of 100 and merge the results.
     */
    fetch: async ids => {
      const batches = chunk(100, ids)
      const resolved = await Promise.all(
        batches.map(async batch => {
          const res = await dashboard20240730Client.listInvoicesLite.request({
            invoiceIds: batch
          })
          return res.data?.items ?? []
        })
      )
      return keyBy(resolved.flat(), 'id')
    }
  },
  renderRow: (id, invoice) => {
    const resolved: InvoiceLite = invoice?.data
      ? invoice
      : {
          id,
          success: false,
          data: { id, invoiceNumber: '', customerLegalCompanyName: '' }
        }

    return (
      <Link
        target="_blank"
        to={`/invoices/${id}`}
        {...Primary13SemiBold}
        color={IndigoIndigo60}
      >
        {titleForInvoice(resolved)}
      </Link>
    )
  }
}
